/**
 * The rail's profile placeholder: initials on a colour picked from the name.
 *
 * Drawn locally rather than fetched from an avatar service, because such a
 * service is sent the operator's name on every page view — a third party
 * learning who works the panel, for the sake of a coloured circle.
 */

/** First letter of the first and last word, uppercased; "?" for no name. */
export function initialsOf(name: string): string {
  const words = name
    .trim()
    .split(/\s+/)
    .filter((word) => word.length > 0);
  if (words.length === 0) return "?";

  // By code point, not code unit, so a letter outside the BMP is not split.
  const first = [...words[0]!][0]!;
  const last = words.length > 1 ? [...words[words.length - 1]!][0]! : "";
  return `${first}${last}`.toUpperCase();
}

/**
 * A stable hue in [0, 360) for a name. FNV-1a over the code points: cheap,
 * deterministic, and spread well enough that a handful of operators rarely
 * share a colour.
 */
export function avatarHue(name: string): number {
  let hash = 0x811c9dc5;
  for (const char of name) {
    hash ^= char.codePointAt(0)!;
    hash = Math.imul(hash, 0x01000193);
  }
  return (hash >>> 0) % 360;
}
